import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Home,
  BookOpen,
  Calendar,
  Inbox,
  Edit,
  Newspaper,
  Star,
  BarChart3,
  Settings,
  Plus,
  Mail,
} from "lucide-react";

/**
 * `keywords` are matched but never shown.
 *
 * A screen is findable here only by the one word someone happened to label it
 * with, and several screens go by a different word in the sidebar than they do
 * in their URL. "Communications" is the worst of them: the sidebar calls it
 * Inbox, the URL calls it communications, and the things people actually come
 * looking for are the email templates and the newsletter, neither of which is
 * a word anywhere in the entry. Typing any of those found nothing.
 *
 * Listing the other names a screen goes by is cheaper than renaming screens
 * and arguing about which name is right.
 */
const NAV_ITEMS = [
  { label: "Dashboard", href: "/admin", icon: Home, keywords: "home overview" },
  { label: "Bookings", href: "/admin/bookings", icon: BookOpen, keywords: "reservations stays guests waivers refunds" },
  { label: "Calendar", href: "/admin/calendar", icon: Calendar, keywords: "availability blackout dates blocked" },
  {
    label: "Communications",
    href: "/admin/communications",
    icon: Inbox,
    keywords: "inbox messages newsletter chat contact replies",
  },
  { label: "Emails", href: "/admin/emails", icon: Mail, keywords: "email templates automated reminders confirmations notifications sending rules editor" },
  { label: "Content Management", href: "/admin/content-management", icon: Edit, keywords: "pages photos amenities copy" },
  { label: "Legal Documents", href: "/admin/legal", icon: Edit, keywords: "terms policies house rules liability waiver" },
  { label: "Blog", href: "/admin/blog", icon: Newspaper, keywords: "posts articles guides categories" },
  { label: "Reviews", href: "/admin/reviews", icon: Star, keywords: "ratings guest feedback testimonials" },
  { label: "Reporting", href: "/admin/reporting", icon: BarChart3, keywords: "reports revenue payouts financials analytics" },
  { label: "Settings", href: "/admin/settings", icon: Settings, keywords: "pricing coupons promotions configuration" },
];

const QUICK_ACTIONS = [
  { label: "New manual booking", href: "/admin/bookings", icon: Plus },
  { label: "Compose email", href: "/admin/communications?tab=emails", icon: Mail },
  { label: "Block calendar dates", href: "/admin/calendar", icon: Calendar },
  { label: "Pricing & coupons", href: "/admin/settings?tab=pricing", icon: Settings },
];

export function AdminCommandPalette() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);

  const isAdmin = location.startsWith("/admin");

  useEffect(() => {
    if (!isAdmin) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isAdmin]);

  if (!isAdmin) return null;

  const navigate = (href: string) => {
    setOpen(false);
    window.location.href = href;
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Search pages and actions..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Quick actions">
          {QUICK_ACTIONS.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem key={item.href + item.label} onSelect={() => navigate(item.href)}>
                <Icon className="mr-2 h-4 w-4" />
                {item.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Navigate">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.href}
                // cmdk filters on `value`, not on what is rendered, so the
                // alternate names ride along without appearing in the list.
                value={`${item.label} ${item.keywords}`}
                onSelect={() => navigate(item.href)}
              >
                <Icon className="mr-2 h-4 w-4" />
                {item.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
      <p className="px-3 py-2 text-xs text-muted-foreground border-t">
        Press <kbd className="px-1.5 py-0.5 font-mono bg-muted border rounded text-[10px]">?</kbd> for keyboard shortcuts
      </p>
    </CommandDialog>
  );
}
